// MessageVideoPlayer.js
import { connect } from 'react-redux';

import getMeData from '../selector/getMeData.js';
import getMessageData from '../selector/getMessageData.js';
import getMessageDrmMediaSource from '../selector/getMessageDrmMediaSource.js';
import getIsAesEncrypted from '../selector/getIsAesEncrypted.js';
import getMessageAesMediaSource from '../selector/getMessageAesMediaSource.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { getSplashVideo } from '../selector/getRemoteConfigComputedData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getMessageThumbnail from '../selector/getMessageThumbnail.js';
import getDrmKeySystemAndRobustness from '../selector/getDrmKeySystemAndRobustness.js';
import getOperationData from '../selector/getOperationData.js';
import getCurrentEngine from '../selector/getCurrentEngine.js';
import fetchDrmLicenseAvailability from '../action/fetchDrmLicenseAvailability.js';
import fetchDrmLicense from '../action/fetchDrmLicense.js';
import fetchMessageDetail from '../action/fetchMessageDetail.js';
import MessageVideoPlayer from '../component/MessageVideoPlayer.jsx';
import checkDrmCapabilityBeforePlay from '../action/checkDrmCapabilityBeforePlay.js';
import fetchMediaMenifestOfMessage from '../action/fetchMediaMenifestOfMessage.js';
import toggleDrmNotSupportAlert from '../action/toggleDrmNotSupportAlert.js';
import setPlayerData from '../action/setPlayerData.js';
import {
  DRM_LICENSE_DURATION,
  WIDEVINE_SERVER_CERTIFICATE_BASE64,
} from '../RemoteConfigKeys.js';
import getAssetData from '../selector/getAssetData.js';
import { getIsOnMobile } from '../resource/getUserAgent.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import {
  COM_WIDEVINE_ALPHA,
  DrmFeatureName,
} from '../resource/drmConstants.js';
import { FEATURE_RESOURCE_DECRYPTION } from '../RemoteConfigKeys.js';

const isOnMobile = getIsOnMobile();

const mapStateToProps = (
  state,
  { messageId, assetId: rawAssetId, drmFeatureName }
) => {
  const token = getMeData(state, 'token');
  const isFlix = getMessageData(state, messageId, 'isFlix');
  const { keySystem, robustness } = getDrmKeySystemAndRobustness(
    state,
    drmFeatureName
  );

  // Swiper uses transforms and overflow: hidden (GPU compositing), which break Widevine L1 secure video path on Android/Chrome.
  // This causes black video frames, so we must use sw_secure_crypto.
  const shouldForceSWDrm =
    isOnMobile &&
    (drmFeatureName === DrmFeatureName.SHORT ||
      drmFeatureName === DrmFeatureName.POST) &&
    keySystem === COM_WIDEVINE_ALPHA;
  const canViewMedia = getMessageData(state, messageId, 'canViewMedia');

  const assetIds = getMessageData(state, messageId, 'assetIds');
  const assetId = rawAssetId || assetIds?.[0];
  const isAesEncrypted = getIsAesEncrypted(state, assetId);
  const mediaType = assetId
    ? getAssetData(state, [assetId], 'contentType')
    : getMessageData(state, messageId, 'mediaType');
  const isAesImage = isAesEncrypted && mediaType?.startsWith('image/');
  // Use `contentType` as reliable signal because `aes` is ambiguous:
  // undefined could mean "not loaded yet" or "not encrypted".
  const isAssetDataLoaded = assetId ? !!mediaType : true;
  const aesImageResourceRelativePath = getAssetData(state, [assetId], 'url');
  const isMessageDetailFetched = !!getNetworkingData(
    state,
    ['messages', messageId, 'detail'],
    'isFetched'
  );
  const shouldFetchMessageDetail = !isMessageDetailFetched;
  const aesImageResourceUrl = aesImageResourceRelativePath
    ? getResourceUrl({
        endpoint: aesImageResourceRelativePath,
        resourceType: PUBLIC,
      }).href
    : undefined;
  const aesImageBlurredRelativePath = getAssetData(
    state,
    [assetId],
    'blurredUrl'
  );
  const aesImageBlurredUrl = aesImageBlurredRelativePath
    ? getResourceUrl({
        endpoint: aesImageBlurredRelativePath,
        resourceType: PUBLIC,
      }).href
    : undefined;

  return {
    meId: getMeData(state, 'id'),
    clientId: getMeData(state, 'clientId'),
    sessionId: getMeData(state, 'sessionId'),
    token,
    keySystem,
    robustness: shouldForceSWDrm ? 'SW_SECURE_CRYPTO' : robustness,
    isAesEncrypted,
    isAesImage,
    isAssetDataLoaded,
    isDecryptionRequired: getRemoteConfigData(
      state,
      FEATURE_RESOURCE_DECRYPTION
    ),
    shouldFetchMessageDetail,
    aesImageResourceUrl,
    aesImageBlurredUrl,
    assetId,
    playbackSource: isAesEncrypted
      ? getMessageAesMediaSource(state, messageId, assetId)
      : getMessageDrmMediaSource(state, messageId, assetId, 'drmMediaSource'),
    canViewMedia,
    isDrmLicenseAvailabilityFetched: getNetworkingData(
      state,
      ['drm', 'licenseAvailability', messageId],
      'isFetched'
    ),
    isFlix,
    viewScope: getMessageData(state, messageId, 'viewScope'),
    mediaType,
    splashVideo: getSplashVideo(state),
    drmLicenseDuration: getRemoteConfigData(state, DRM_LICENSE_DURATION),
    drmTestStatus: getOperationData(state, ['drm'], 'testStatus'),
    thumbnail: getMessageThumbnail(
      state,
      messageId,
      assetId,
      '512x512',
      'jpg',
      canViewMedia ? 'clear' : 'blurred'
    ),
    widevineServerCertificateBase64: getRemoteConfigData(
      state,
      WIDEVINE_SERVER_CERTIFICATE_BASE64
    ),
    currentEngine: getCurrentEngine(state),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    setPlayerData: ({ isMuted }) =>
      dispatch(setPlayerData({ isMuted, isFirstUnmuted: false })),
    fetchDrmLicenseAvailability: data =>
      dispatch(fetchDrmLicenseAvailability(data)),
    fetchDrmLicense: data => dispatch(fetchDrmLicense(data)),
    // Directly use the DrmNotSupport alert since the original wording is neutral
    openPlaybackNotSupportAlert: () => dispatch(toggleDrmNotSupportAlert()),
    checkDrmCapabilityBeforePlay: ({ drmFeatureName }) =>
      dispatch(checkDrmCapabilityBeforePlay({ drmFeatureName })),
    fetchMediaMenifestOfMessage: ({ id }) =>
      dispatch(fetchMediaMenifestOfMessage({ id })),
    fetchMessageDetail: ({ id }) => dispatch(fetchMessageDetail({ id })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MessageVideoPlayer);
